// Utilities for the "agreement details" block that is edited in the proposal
// / change-order preview panel and saved on the Proposal / ChangeOrder
// documents.
//
// The block covers:
//   • Approximate Start / Completion / Duration on Page 1
//   • Progress-payment "upon completion of..." text on Page 3
//   • The Agreement Costs table (7 rows × 4 columns) on Page 3
//   • The "not later than midnight of ___" deadline on the two Right-to-
//     Cancel pages
//
// Every field is a short string, so partial dates, "TBD", "included", check
// numbers with dashes, etc. round-trip untouched.

export const AGREEMENT_COST_ROWS = [
  { key: 'downPayment',      label: 'Down Payment' },
  { key: 'mobilization',     label: 'Mobilization Payment' },
  { key: 'progressPayment',  label: 'Progress Payment' },
  { key: 'balance',          label: 'Balance at Completion' },
  { key: 'total',            label: 'TOTAL' },
  { key: 'buildingPermit',   label: 'Building Permit TBD' },
  { key: 'permittingService',label: 'Permitting Service TBD' }
];

export const AGREEMENT_COST_COLS = [
  { key: 'amount',      label: 'Amount' },
  { key: 'datePaid',    label: 'Date Paid' },
  { key: 'checkNumber', label: 'Check #' },
  { key: 'initialed',   label: 'Initialed' }
];

const emptyCostRow = () => ({ amount: '', datePaid: '', checkNumber: '', initialed: '' });

export const EMPTY_AGREEMENT_DETAILS = Object.freeze({
  approxStartDate: '',
  approxCompletionDate: '',
  approxDuration: '',
  progressPaymentUpon: '',
  cancellationDeadline: '',
  agreementCosts: {
    downPayment:      emptyCostRow(),
    mobilization:     emptyCostRow(),
    progressPayment:  emptyCostRow(),
    balance:          emptyCostRow(),
    total:            emptyCostRow(),
    buildingPermit:   emptyCostRow(),
    permittingService:emptyCostRow()
  }
});

// Coerce whatever the server returns (Mongoose subdoc, POJO, or undefined)
// into a fresh, always-populated `{ ..., agreementCosts: { row: {col:''} } }`
// so form controls can bind to any nested field without null checks.
export const normaliseAgreementDetails = (input) => {
  const src = input || {};
  const costsIn = src.agreementCosts || {};
  const agreementCosts = {};
  AGREEMENT_COST_ROWS.forEach(({ key }) => {
    const rowIn = costsIn[key] || {};
    agreementCosts[key] = {
      amount: rowIn.amount || '',
      datePaid: rowIn.datePaid || '',
      checkNumber: rowIn.checkNumber || '',
      initialed: rowIn.initialed || ''
    };
  });
  return {
    approxStartDate: src.approxStartDate || '',
    approxCompletionDate: src.approxCompletionDate || '',
    approxDuration: src.approxDuration || '',
    progressPaymentUpon: src.progressPaymentUpon || '',
    cancellationDeadline: src.cancellationDeadline || '',
    agreementCosts
  };
};

// Immutably update a scalar field (approxStartDate, ...).
export const setScalarField = (details, field, value) => ({
  ...details,
  [field]: value
});

// Immutably update one cell in the Agreement Costs table.
export const setCostField = (details, row, col, value) => ({
  ...details,
  agreementCosts: {
    ...details.agreementCosts,
    [row]: {
      ...(details.agreementCosts?.[row] || emptyCostRow()),
      [col]: value
    }
  }
});

// Which scalar fields are calendar dates (rendered as `<input type="date">`
// in edit mode) vs. free text (rendered as contenteditable spans).
const DATE_SCALAR_FIELDS = new Set([
  'approxStartDate',
  'approxCompletionDate',
  'cancellationDeadline'
]);
// Cost-table columns that are calendar dates.
const DATE_COST_COLS = new Set(['datePaid']);

// Try to normalise a stored value into the `YYYY-MM-DD` shape that
// `<input type="date">` requires. Handles both "8/15/2026" (what the picker
// commits) and "2026-08-15" (raw picker output) inputs; returns '' when the
// input can't be parsed so an empty picker shows up instead of "Invalid Date".
const toIsoDate = (raw) => {
  if (raw === undefined || raw === null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymd) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Format a `YYYY-MM-DD` value into the `M/D/YYYY` shape we commit to the
// stored agreement details. We store the display format directly so the
// PDF, print view, and public proposal page all show pretty dates without
// having to re-parse the stored value.
const formatIsoForDisplay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return iso || '';
  const [, y, mo, da] = m;
  return `${Number(mo)}/${Number(da)}/${y}`;
};

// Wire up inline-editing on all `[data-agreement-field]` and
// `[data-agreement-cost]` slots inside `container`.
//
// Every slot becomes editable: date-typed fields get a real
// `<input type="date">` (native OS date picker), everything else becomes
// contenteditable with a subtle highlight. Commits fire on `change`
// (date picker) or `blur` (text). Enter blurs a text field to commit.
//
// Returns a cleanup function that removes listeners, injected inputs and
// editable attributes.
export const attachInlineAgreementEditor = (container, { onScalarCommit, onCostCommit } = {}) => {
  if (!container || typeof container.querySelectorAll !== 'function') {
    return () => {};
  }

  const scalarSlots = Array.from(container.querySelectorAll('[data-agreement-field]'));
  const costSlots   = Array.from(container.querySelectorAll('[data-agreement-cost]'));

  if (scalarSlots.length === 0 && costSlots.length === 0) return () => {};

  const handlers = [];

  const decorateEditable = (slot, { minWidth = '6ch' } = {}) => {
    slot.setAttribute('contenteditable', 'true');
    slot.setAttribute('spellcheck', 'false');
    // Don't override `display` when the slot is a real table cell — HTML
    // tables lay their children out with the intrinsic `table-cell`
    // display, and forcing `inline-block` collapses every column into the
    // width of one cell. For non-<td>/<th> slots (spans, divs) an
    // `inline-block` gives us a stable clickable target with a minimum
    // width for empty fields.
    const isTableCell = slot.tagName === 'TD' || slot.tagName === 'TH';
    if (!isTableCell) {
      slot.style.display = 'inline-block';
      slot.style.minWidth = minWidth;
    }
    slot.style.minHeight = '1em';
    slot.style.padding = isTableCell ? '4px 6px' : '0 4px';
    slot.style.background = 'rgba(59, 130, 246, 0.08)';
    slot.style.borderRadius = '2px';
    slot.style.outline = 'none';
    slot.style.cursor = 'text';
    if (!slot.title) slot.title = 'Click to edit';
  };

  const enterCommits = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
  };

  // Mount a native `<input type="date">` inside `slot`, seeded from the
  // slot's current text (parsed via toIsoDate). The commit callback gets
  // the pretty M/D/YYYY string so what we store matches what we print.
  const mountDatePicker = (slot, onCommit) => {
    const isTableCell = slot.tagName === 'TD' || slot.tagName === 'TH';
    const currentText = (slot.textContent || '').trim();

    const input = document.createElement('input');
    input.type = 'date';
    input.value = toIsoDate(currentText);
    input.setAttribute('data-agreement-date-input', 'true');

    // Style so the picker blends into the surrounding document rather than
    // looking like a form control.
    input.style.font = 'inherit';
    input.style.color = 'inherit';
    input.style.border = 'none';
    input.style.outline = 'none';
    input.style.background = 'rgba(59, 130, 246, 0.08)';
    input.style.borderRadius = '2px';
    input.style.padding = isTableCell ? '2px 4px' : '0 4px';
    input.style.minHeight = '1em';
    if (isTableCell) {
      input.style.width = '100%';
      input.style.boxSizing = 'border-box';
    }

    slot.textContent = '';
    slot.appendChild(input);
    // Match the surrounding text: unstyled cells still get a subtle
    // highlight around the picker so users see it's clickable.
    slot.style.padding = isTableCell ? '4px 6px' : '0';
    slot.style.cursor = 'pointer';

    const onChange = () => {
      const pretty = formatIsoForDisplay(input.value);
      if (typeof onCommit === 'function') onCommit(pretty);
    };
    input.addEventListener('change', onChange);
    handlers.push({ slot, input, onChange });
  };

  scalarSlots.forEach((slot) => {
    const field = slot.getAttribute('data-agreement-field');
    if (!field) return;
    if (DATE_SCALAR_FIELDS.has(field)) {
      mountDatePicker(slot, (value) => {
        if (typeof onScalarCommit === 'function') onScalarCommit(field, value);
      });
      return;
    }
    decorateEditable(slot, { minWidth: '8ch' });
    const onBlur = (event) => {
      const value = (event.target.textContent || '').trim();
      if (typeof onScalarCommit === 'function') onScalarCommit(field, value);
    };
    slot.addEventListener('blur', onBlur);
    slot.addEventListener('keydown', enterCommits);
    handlers.push({ slot, onBlur });
  });

  costSlots.forEach((slot) => {
    const path = slot.getAttribute('data-agreement-cost');
    if (!path) return;
    const [row, col] = path.split('.');
    if (!row || !col) return;
    if (DATE_COST_COLS.has(col)) {
      mountDatePicker(slot, (value) => {
        if (typeof onCostCommit === 'function') onCostCommit(row, col, value);
      });
      return;
    }
    decorateEditable(slot, { minWidth: '6ch' });
    const onBlur = (event) => {
      const value = (event.target.textContent || '').trim();
      if (typeof onCostCommit === 'function') onCostCommit(row, col, value);
    };
    slot.addEventListener('blur', onBlur);
    slot.addEventListener('keydown', enterCommits);
    handlers.push({ slot, onBlur });
  });

  return () => {
    handlers.forEach(({ slot, onBlur, input, onChange }) => {
      if (input) {
        input.removeEventListener('change', onChange);
        // Restore the picker's committed value as plain text so anything
        // hooked after this (e.g. the next re-render's initial paint)
        // sees the current display string.
        const pretty = formatIsoForDisplay(input.value);
        slot.textContent = pretty;
      } else if (onBlur) {
        slot.removeEventListener('blur', onBlur);
        slot.removeEventListener('keydown', enterCommits);
        slot.removeAttribute('contenteditable');
      }
      slot.style.background = '';
      slot.style.cursor = '';
      slot.style.display = '';
      slot.style.minWidth = '';
      slot.style.minHeight = '';
      slot.style.padding = '';
      slot.style.borderRadius = '';
      slot.style.outline = '';
    });
  };
};
